import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Alert,
  Divider
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { APIProvider } from '@vis.gl/react-google-maps';
import PlaceAutocomplete from '../common/PlaceAutocomplete';

import { 
  fetchRecurringRides, 
  updateRecurringOrder 
} from '../../redux/slices/ridesSlice';
import { VEHICLE_TYPE_OPTIONS } from '../../config/vehicleTypes';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';

const WEEKDAYS = [
  { value: 0, label: 'Sonntag' },
  { value: 1, label: 'Montag' },
  { value: 2, label: 'Dienstag' },
  { value: 3, label: 'Mittwoch' },
  { value: 4, label: 'Donnerstag' },
  { value: 5, label: 'Freitag' },
  { value: 6, label: 'Samstag' }
];

const RecurringOrderEditForm = () => {
  const { recurringOrderId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { recurringOrders, isLoading, error } = useSelector(state => state.rides);

  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    start: '',
    destination: '',
    start_location: { latitude: null, longitude: null },
    destination_location: { latitude: null, longitude: null },
    vehicle_type: 1,
    recurring_weekdays: [],
    recurring_time: new Date().setHours(8, 0, 0, 0),
    current_end_date: null,
    new_end_date: null
  });

  const [currentOrder, setCurrentOrder] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    // Lade Daueraufträge wenn noch nicht geladen
    if (recurringOrders.length === 0) {
      dispatch(fetchRecurringRides());
    }
  }, [dispatch, recurringOrders.length]);

  useEffect(() => {
    // Finde den aktuellen Dauerauftrag
    const order = recurringOrders.find(o => o.id === recurringOrderId);
    
    if (order) {
      setCurrentOrder(order);
      
      // Extrahiere Daten aus den Fahrten
      const firstRide = order.rides?.[0];
      if (firstRide) {
        // Ermittle die Wochentage aus allen Fahrten
        const weekdays = [...new Set(order.rides.map(ride => {
          // Konvertiere Firestore Timestamp zu JavaScript Date
          const rideDate = ride.scheduledTime?.toDate ? ride.scheduledTime.toDate() : new Date(ride.scheduledTime);
          return rideDate.getDay();
        }))];
        
        // Ermittle das aktuelle Enddatum (letzte geplante Fahrt)
        const sortedRides = order.rides
          .filter(ride => ['scheduled', 'pre_accepted'].includes(ride.status))
          .sort((a, b) => {
            // Konvertiere Firestore Timestamps zu JavaScript Dates für Vergleich
            const dateA = a.scheduledTime?.toDate ? a.scheduledTime.toDate() : new Date(a.scheduledTime);
            const dateB = b.scheduledTime?.toDate ? b.scheduledTime.toDate() : new Date(b.scheduledTime);
            return dateB - dateA;
          });
        
        const lastRideDate = sortedRides[0] ? 
          (sortedRides[0].scheduledTime?.toDate ? sortedRides[0].scheduledTime.toDate() : new Date(sortedRides[0].scheduledTime)) : 
          new Date();
        
        setFormData({
          customer_name: firstRide.customerName || '',
          customer_phone: firstRide.customerPhone || '',
          start: firstRide.start || '',
          destination: firstRide.destination || '',
          start_location: { 
            latitude: firstRide.startLatitude || null, 
            longitude: firstRide.startLongitude || null 
          },
          destination_location: { 
            latitude: firstRide.destinationLatitude || null, 
            longitude: firstRide.destinationLongitude || null 
          },
          vehicle_type: firstRide.vehicleType || 1,
          recurring_weekdays: weekdays,
          recurring_time: (firstRide.scheduledTime?.toDate ? firstRide.scheduledTime.toDate() : new Date(firstRide.scheduledTime)).getTime(),
          current_end_date: lastRideDate,
          new_end_date: lastRideDate
        });
      }
    }
  }, [recurringOrders, recurringOrderId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleWeekdayChange = (weekday) => {
    setFormData(prev => ({
      ...prev,
      recurring_weekdays: prev.recurring_weekdays.includes(weekday)
        ? prev.recurring_weekdays.filter(w => w !== weekday)
        : [...prev.recurring_weekdays, weekday]
    }));
  };

  const handleTimeChange = (e) => {
    const timeString = e.target.value; // "HH:MM"
    const [hours, minutes] = timeString.split(':').map(Number);
    const timeDate = new Date();
    timeDate.setHours(hours, minutes, 0, 0);
    
    setFormData(prev => ({
      ...prev,
      recurring_time: timeDate.getTime()
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Verwende aktuelle Koordinaten aus formData (von PlaceAutocomplete gesetzt)
      let startCoords = {};
      let destCoords = {};
      
      // Nutze die von PlaceAutocomplete gesetzten Koordinaten wenn verfügbar
      if (formData.start_location && formData.start_location.latitude) {
        startCoords = {
          startLatitude: formData.start_location.latitude,
          startLongitude: formData.start_location.longitude
        };
      } else {
        // Fallback zu bestehenden Koordinaten
        const firstRide = currentOrder.rides?.[0];
        if (firstRide) {
          startCoords = {
            startLatitude: firstRide.startLatitude,
            startLongitude: firstRide.startLongitude
          };
        }
      }
      
      if (formData.destination_location && formData.destination_location.latitude) {
        destCoords = {
          destinationLatitude: formData.destination_location.latitude,
          destinationLongitude: formData.destination_location.longitude
        };
      } else {
        // Fallback zu bestehenden Koordinaten
        const firstRide = currentOrder.rides?.[0];
        if (firstRide) {
          destCoords = {
            destinationLatitude: firstRide.destinationLatitude,
            destinationLongitude: firstRide.destinationLongitude
          };
        }
      }

      // Bereite Update-Daten vor (Kundenname bleibt unverändert, Telefon kann aktualisiert werden)
      const updateData = {
        customerId: currentOrder.rides[0]?.customerId,
        customerPhone: formData.customer_phone,
        start: formData.start,
        destination: formData.destination,
        vehicleType: formData.vehicle_type,
        ...startCoords,
        ...destCoords
      };

        // Prüfe ob das Enddatum verlängert wurde
        const currentEndDate = new Date(formData.current_end_date);
        const newEndDate = new Date(formData.new_end_date);
        
        if (newEndDate > currentEndDate) {
          updateData.newEndDate = newEndDate;
          updateData.recurring_weekdays = formData.recurring_weekdays;
          updateData.recurring_time = new Date(formData.recurring_time);
        }

        // Führe Update durch
        const result = await dispatch(updateRecurringOrder({
          recurringOrderId,
          updateData
        }));

        if (result.error) {
          throw new Error(result.error.message || 'Fehler beim Aktualisieren');
        }

        // Lade Daueraufträge neu
        await dispatch(fetchRecurringRides());

      // Erfolgreiche Aktualisierung
      alert('Dauerauftrag erfolgreich aktualisiert!');
      navigate('/fahrten/dauerauftraege');
    } catch (error) {
      console.error('Fehler beim Aktualisieren:', error);
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading && !currentOrder) {
    return <Loading message="Dauerauftrag wird geladen..." />;
  }

  if (error) {
    return (
      <ErrorMessage
        title="Fehler beim Laden des Dauerauftrags"
        message={error}
        onRetry={() => dispatch(fetchRecurringRides())}
      />
    );
  }

  if (!currentOrder) {
    return (
      <ErrorMessage
        title="Dauerauftrag nicht gefunden"
        message="Der angeforderte Dauerauftrag konnte nicht gefunden werden."
      />
    );
  }

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  const formatDateForInput = (date) => {
    if (!date) return '';
    
    // Stelle sicher, dass es ein gültiges Date-Objekt ist
    const dateObj = date instanceof Date ? date : new Date(date);
    
    // Prüfe ob das Date-Objekt gültig ist
    if (isNaN(dateObj.getTime())) return '';
    
    return dateObj.toISOString().split('T')[0];
  };

  return (
    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <PageHeader
        title="Dauerauftrag bearbeiten"
        subtitle={`Bearbeitung für ${currentOrder.customer_name}`}
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Fahrten', path: '/fahrten' },
          { label: 'Daueraufträge', path: '/fahrten/dauerauftraege' },
          { label: 'Bearbeiten' }
        ]}
      />

      <Paper sx={{ p: 3, mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/fahrten/dauerauftraege')}
            sx={{ mr: 2 }}
          >
            Zurück zur Übersicht
          </Button>
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Kundeninformationen */}
            <Grid size={12}>
              <Typography variant="h6" gutterBottom>
                Kundeninformationen
              </Typography>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                name="customer_name"
                label="Kundenname"
                value={formData.customer_name}
                disabled
                sx={{ 
                  '& .MuiInputBase-root': { 
                    backgroundColor: 'grey.100' 
                  } 
                }}
                helperText="Kundendaten können nicht geändert werden"
              />
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                name="customer_phone"
                label="Telefonnummer"
                value={formData.customer_phone}
                onChange={handleInputChange}
                helperText="Telefonnummer kann aktualisiert werden"
              />
            </Grid>

            {/* Route */}
            <Grid size={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                <LocationIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Route
              </Typography>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <PlaceAutocomplete
                fullWidth
                label="Startadresse"
                value={formData.start}
                onChange={(e) => setFormData(prev => ({ ...prev, start: e.target.value }))}
                onPlaceSelect={(address, location) => {
                  setFormData(prev => ({ 
                    ...prev, 
                    start: address,
                    start_location: location
                  }));
                }}
                required
              />
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <PlaceAutocomplete
                fullWidth
                label="Zieladresse"
                value={formData.destination}
                onChange={(e) => setFormData(prev => ({ ...prev, destination: e.target.value }))}
                onPlaceSelect={(address, location) => {
                  setFormData(prev => ({ 
                    ...prev, 
                    destination: address,
                    destination_location: location
                  }));
                }}
                required
              />
            </Grid>

            {/* Fahrzeugtyp */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Fahrzeugtyp</InputLabel>
                <Select
                  name="vehicle_type"
                  value={formData.vehicle_type}
                  onChange={handleInputChange}
                  label="Fahrzeugtyp"
                >
                  {VEHICLE_TYPE_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Zeiten und Wiederholung */}
            <Grid size={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Zeitplan
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="time"
                name="recurring_time"
                label="Uhrzeit"
                value={formatTime(formData.recurring_time)}
                onChange={handleTimeChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={12}>
              <Typography variant="body1" gutterBottom>
                Wochentage
              </Typography>
              <FormGroup row>
                {WEEKDAYS.map((day) => (
                  <FormControlLabel
                    key={day.value}
                    control={
                      <Checkbox
                        checked={formData.recurring_weekdays.includes(day.value)}
                        onChange={() => handleWeekdayChange(day.value)}
                      />
                    }
                    label={day.label}
                  />
                ))}
              </FormGroup>
            </Grid>

            {/* Datumsbereich */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="date"
                name="current_end_date"
                label="Aktuelles Enddatum"
                value={formatDateForInput(formData.current_end_date)}
                InputLabelProps={{ shrink: true }}
                disabled
                helperText="Das aktuelle Enddatum des Dauerauftrags"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="date"
                name="new_end_date"
                label="Neues Enddatum"
                value={formatDateForInput(formData.new_end_date)}
                onChange={(e) => {
                  const dateValue = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    new_end_date: dateValue ? new Date(dateValue) : null
                  }));
                }}
                InputLabelProps={{ shrink: true }}
                helperText="Verlängern Sie das Enddatum, um neue Fahrten zu erstellen"
              />
            </Grid>

            {/* Fehleranzeige */}
            {submitError && (
              <Grid size={12}>
                <Alert severity="error">
                  {submitError}
                </Alert>
              </Grid>
            )}

            {/* Submit Button */}
            <Grid size={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={isSubmitting}
                  size="large"
                >
                  {isSubmitting ? 'Wird gespeichert...' : 'Änderungen speichern'}
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={() => navigate('/fahrten/dauerauftraege')}
                  disabled={isSubmitting}
                  size="large"
                >
                  Abbrechen
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
      </Box>
    </APIProvider>
  );
};

export default RecurringOrderEditForm;